import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, ShoppingCart, Bot, MapPin, DollarSign, Send, ArrowRight, MessageSquare, ShieldCheck, CheckCircle2, Key, ListChecks, Sparkles, Check, Truck, Store, AlertTriangle } from 'lucide-react';
import { cn } from '../lib/utils';
import { Language, SubscriptionTier, GroceryItem } from '../types';
import { StoreLoginModal } from './StoreLoginModal';
import { toast } from 'sonner';

export type GroceryListMode = 'all_ai' | 'include_grocery' | 'only_grocery';

export interface StoreThresholdConfig {
  minDeliverySum: number;
  freeDeliveryThreshold?: number;
  deliveryFee: number;
  pickupAvailable: boolean;
}

export const STORE_THRESHOLDS: Record<string, StoreThresholdConfig> = {
  silpo: { minDeliverySum: 400, freeDeliveryThreshold: 1500, deliveryFee: 50, pickupAvailable: true },
  atb: { minDeliverySum: 400, freeDeliveryThreshold: 2000, deliveryFee: 50, pickupAvailable: true },
  gastronom_1: { minDeliverySum: 250, freeDeliveryThreshold: 1000, deliveryFee: 45, pickupAvailable: true },
  sim23: { minDeliverySum: 200, freeDeliveryThreshold: 800, deliveryFee: 40, pickupAvailable: true },
  velmart: { minDeliverySum: 350, freeDeliveryThreshold: 1200, deliveryFee: 50, pickupAvailable: true },
  metro: { minDeliverySum: 500, freeDeliveryThreshold: 2500, deliveryFee: 65, pickupAvailable: true },
  fozzy: { minDeliverySum: 500, freeDeliveryThreshold: 2000, deliveryFee: 60, pickupAvailable: true },
  instacart: { minDeliverySum: 35, freeDeliveryThreshold: 99, deliveryFee: 5.99, pickupAvailable: true },
  amazon_fresh: { minDeliverySum: 35, freeDeliveryThreshold: 100, deliveryFee: 5.99, pickupAvailable: false },
  walmart: { minDeliverySum: 35, freeDeliveryThreshold: 75, deliveryFee: 5.99, pickupAvailable: true },
};

interface NutriAgentConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLaunch: (config: NutriAgentLaunchConfig) => void;
  lang: Language;
  theme: 'light' | 'dark';
  illumination?: boolean;
  userTier: SubscriptionTier;
  groceryItems?: GroceryItem[];
  initialGroceryListMode?: GroceryListMode;
  initialConfig?: NutriAgentLaunchConfig | null;
}

export interface NutriAgentLaunchConfig {
  budgetLimitUSD: number;
  currency: string;
  currencySymbol: string;
  targetStores: string[];
  messengerPlatform: string[];
  notificationChannels: string[];
  userNotes?: string;
  groceryListMode?: GroceryListMode;
  groceryItems?: Array<{ id?: string; name: string; amount?: string; unit?: string; checked?: boolean }>;
  fulfillmentMethod?: 'delivery' | 'pickup';
}

export const NutriAgentConfigModal: React.FC<NutriAgentConfigModalProps> = ({
  isOpen,
  onClose,
  onLaunch,
  lang,
  theme,
  illumination,
  userTier,
  groceryItems = [],
  initialGroceryListMode = 'include_grocery',
  initialConfig = null
}) => {
  const [budget, setBudget] = useState<number | ''>(() => {
    if (initialConfig && typeof initialConfig.budgetLimitUSD === 'number' && initialConfig.budgetLimitUSD > 0) {
      return initialConfig.budgetLimitUSD;
    }
    return lang === 'ua' ? 3000 : 80;
  });
  const [currency, setCurrency] = useState<string>(() => initialConfig?.currency || (lang === 'ua' ? 'UAH' : 'USD'));
  const [storeRegion, setStoreRegion] = useState<'ua' | 'intl'>('ua');
  const [store, setStore] = useState<string>(() => initialConfig?.targetStores?.[0] || (lang === 'ua' ? 'silpo' : 'instacart'));
  const [groceryListMode, setGroceryListMode] = useState<GroceryListMode>(() => initialConfig?.groceryListMode || initialGroceryListMode);
  const [fulfillmentMethod, setFulfillmentMethod] = useState<'delivery' | 'pickup'>(() => initialConfig?.fulfillmentMethod || 'delivery');
  const [userNotes, setUserNotes] = useState<string>(() => initialConfig?.userNotes || '');
  const [notificationChannels] = useState<string[]>(['app_push']);
  const [connectedStoreData, setConnectedStoreData] = useState<{
    type?: 'app' | 'web';
    email?: string;
    phone?: string;
    cardId?: string;
    appBound?: boolean;
  } | null>(() => {
    try {
      const stores = JSON.parse(localStorage.getItem('nutri_agent_google_stores') || '{}');
      return stores[store] || null;
    } catch {
      return null;
    }
  });
  const [isStoreLoginOpen, setIsStoreLoginOpen] = useState<boolean>(false);
  const [isAutoToppedUp, setIsAutoToppedUp] = useState<boolean>(false);

  useEffect(() => {
    setIsAutoToppedUp(false);
  }, [store, currency, fulfillmentMethod]);

  useEffect(() => {
    if (isOpen) {
      if (initialConfig) {
        if (initialConfig.targetStores?.[0]) {
          setStore(initialConfig.targetStores[0]);
          if (['atb', 'silpo', 'fozzy'].includes(initialConfig.targetStores[0].toLowerCase())) {
            setStoreRegion('ua');
          } else {
            setStoreRegion('intl');
          }
        }
        if (initialConfig.currency) setCurrency(initialConfig.currency);
        if (typeof initialConfig.budgetLimitUSD === 'number' && initialConfig.budgetLimitUSD > 0) {
          setBudget(initialConfig.budgetLimitUSD);
        }
        if (initialConfig.groceryListMode) setGroceryListMode(initialConfig.groceryListMode);
        if (initialConfig.userNotes) setUserNotes(initialConfig.userNotes);
        if (initialConfig.fulfillmentMethod) setFulfillmentMethod(initialConfig.fulfillmentMethod);
      } else if (initialGroceryListMode) {
        setGroceryListMode(initialGroceryListMode);
      }
    }
  }, [isOpen, initialConfig, initialGroceryListMode]);

  useEffect(() => {
    try {
      const stores = JSON.parse(localStorage.getItem('nutri_agent_google_stores') || '{}');
      const storeData = stores[store];
      if (storeData) {
        setConnectedStoreData(storeData);
      } else {
        const legacyUser = localStorage.getItem('nutri_agent_user');
        if (legacyUser) {
          setConnectedStoreData({ type: 'web', email: legacyUser });
        } else {
          setConnectedStoreData(null);
        }
      }
    } catch {
      setConnectedStoreData(null);
    }
  }, [store, isStoreLoginOpen]);

  const activeGroceryItems = React.useMemo(() => {
    if (initialConfig?.groceryItems && initialConfig.groceryItems.length > 0) {
      return initialConfig.groceryItems;
    }
    if (groceryItems && groceryItems.length > 0) return groceryItems;
    try {
      const saved = localStorage.getItem('grocery_items');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  }, [groceryItems, initialConfig, isOpen]);

  const unboughtItems = React.useMemo(() => {
    const unbought = activeGroceryItems.filter((i: any) => !i.checked);
    return unbought.length > 0 ? unbought : activeGroceryItems;
  }, [activeGroceryItems]);

  const getEstimatedItemPrice = (name: string, isUaPrice: boolean): number => {
    const n = name.toLowerCase();
    if (isUaPrice) {
      if (/філе|beef|яловичин|стейк|лосось|salmon|сир тверд/i.test(n)) return 210.00;
      if (/курк|риба|тунець|сир кисломолоч|моцарел|індич/i.test(n)) return 125.00;
      if (/молок|мигдал|вівсян|олія|масло/i.test(n)) return 85.00;
      if (/яйц/i.test(n)) return 65.00;
      if (/банан|яблук|огірок|помідор|шпинат|броколі|авокадо/i.test(n)) return 70.00;
      if (/хліб|лаваш/i.test(n)) return 35.00;
      return 60.00;
    } else {
      if (/філе|beef|steak|salmon/i.test(n)) return 14.99;
      if (/chicken|fish|turkey|cheese/i.test(n)) return 8.99;
      if (/milk|almond|oats|oil/i.test(n)) return 4.99;
      if (/egg/i.test(n)) return 6.49;
      if (/banana|apple|spinach|avocado/i.test(n)) return 3.99;
      return 4.49;
    }
  };

  const estimatedListTotal = React.useMemo(() => {
    const isUaCurrency = currency === 'UAH';
    const deliveryFee = isUaCurrency ? 50.00 : 5.99;
    const itemsSum = unboughtItems.reduce((acc, item: any) => {
      const qty = item.amount ? Math.max(1, parseInt(item.amount) || 1) : 1;
      return acc + getEstimatedItemPrice(item.name, isUaCurrency) * qty;
    }, 0);
    return itemsSum > 0 ? Math.ceil(itemsSum + deliveryFee) : (isUaCurrency ? 3000 : 80);
  }, [unboughtItems, currency]);

  const CURRENCIES: Record<string, { symbol: string; label: string }> = {
    UAH: { symbol: '₴', label: 'UAH (₴)' },
    USD: { symbol: '$', label: 'USD ($)' },
  };

  const UKRAINIAN_STORES = [
    { id: 'atb', label: 'АТБ' },
    { id: 'silpo', label: 'Сільпо' },
    { id: 'fozzy', label: 'Fozzy' },
  ];

  const INTERNATIONAL_STORES = [
    { id: 'instacart', label: 'Instacart' },
    { id: 'amazon_fresh', label: 'Amazon Fresh' },
    { id: 'walmart', label: 'Walmart Grocery' },
  ];

  const availableCurrencies = storeRegion === 'ua' ? ['UAH'] : ['USD'];

  const storeThreshold = React.useMemo(() => {
    return STORE_THRESHOLDS[store.toLowerCase()] || {
      minDeliverySum: currency === 'UAH' ? 400 : 35,
      freeDeliveryThreshold: currency === 'UAH' ? 1500 : 100,
      deliveryFee: currency === 'UAH' ? 50 : 5.99,
      pickupAvailable: true
    };
  }, [store, currency]);

  const itemsSubtotal = React.useMemo(() => {
    const isUaCurrency = currency === 'UAH';
    return unboughtItems.reduce((acc, item: any) => {
      const qty = item.amount ? Math.max(1, parseInt(item.amount) || 1) : 1;
      return acc + getEstimatedItemPrice(item.name, isUaCurrency) * qty;
    }, 0);
  }, [unboughtItems, currency]);

  const effectiveSubtotal = isAutoToppedUp ? Math.max(itemsSubtotal, storeThreshold.minDeliverySum) : itemsSubtotal;
  const isBelowMinDelivery = fulfillmentMethod === 'delivery' && !isAutoToppedUp && itemsSubtotal > 0 && itemsSubtotal < storeThreshold.minDeliverySum;
  const deficitToMin = Math.max(0, storeThreshold.minDeliverySum - effectiveSubtotal);
  const progressPercent = isAutoToppedUp ? 100 : Math.min(100, Math.round((itemsSubtotal / storeThreshold.minDeliverySum) * 100));

  const handleAutoTopUpWithStaples = () => {
    setIsAutoToppedUp(true);
    handleSetGroceryListMode('include_grocery');
    const deficit = Math.max(0, storeThreshold.minDeliverySum - itemsSubtotal);
    const targetBudget = Math.max(storeThreshold.minDeliverySum + storeThreshold.deliveryFee, estimatedListTotal + deficit);
    setBudget(targetBudget);
    toast.success(
      lang === 'ua'
        ? `Кошик доповнено дефіцитними продуктами (яйця, вівсянка, вода) до мінімуму доставки (${currentSymbol}${storeThreshold.minDeliverySum})!`
        : `Cart topped up with healthy nutrient staples (eggs, oats, mineral water) to reach delivery minimum (${currentSymbol}${storeThreshold.minDeliverySum})!`
    );
  };

  const handleSetGroceryListMode = (mode: GroceryListMode) => {
    setGroceryListMode(mode);
    if (mode === 'only_grocery') {
      setBudget(estimatedListTotal);
    } else if (budget === estimatedListTotal) {
      setBudget(currency === 'UAH' ? 3000 : 80);
    }
  };

  const handleSetStoreRegion = (region: 'ua' | 'intl') => {
    setStoreRegion(region);
    if (region === 'ua') {
      setStore('atb');
      setCurrency('UAH');
      if (budget === 80 || budget === '') {
        setBudget(groceryListMode === 'only_grocery' ? estimatedListTotal : 3000);
      }
    } else {
      setStore('instacart');
      setCurrency('USD');
      if (budget === 3000 || budget === '') {
        setBudget(groceryListMode === 'only_grocery' ? estimatedListTotal : 80);
      }
    }
  };

  const handleSelectStore = (storeId: string, isUaStore: boolean) => {
    setStore(storeId);
    if (isUaStore && currency !== 'UAH' && (budget === 80 || budget === '')) {
      setCurrency('UAH');
      setBudget(groceryListMode === 'only_grocery' ? estimatedListTotal : 3000);
    } else if (!isUaStore && currency === 'UAH' && (budget === 3000 || budget === '')) {
      setCurrency('USD');
      setBudget(groceryListMode === 'only_grocery' ? estimatedListTotal : 80);
    }
  };

  React.useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const currentSymbol = CURRENCIES[currency]?.symbol || (currency === 'UAH' ? '₴' : '$');

  const getStoreDisplayName = (storeId: string) => {
    const all = [...UKRAINIAN_STORES, ...INTERNATIONAL_STORES];
    return all.find(s => s.id === storeId)?.label || storeId.toUpperCase();
  };

  const getConnectedAccountLabel = () => {
    if (!connectedStoreData) {
      return lang === 'ua' ? 'Акаунт або додаток не підключено' : 'No Store Account / App Connected';
    }
    if (connectedStoreData.type === 'app' || connectedStoreData.appBound) {
      const id = connectedStoreData.phone || connectedStoreData.cardId || connectedStoreData.email || '';
      return lang === 'ua' 
        ? `📱 Додаток зв'язано: ${id}` 
        : `📱 Branded App Linked: ${id}`;
    }
    const id = connectedStoreData.email || connectedStoreData.phone || '';
    return lang === 'ua' 
      ? `🌐 Веб / Google: ${id}` 
      : `🌐 Web / Google: ${id}`;
  };

  return (
    <>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="nutriagent-config-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        >
          <motion.div
            key="nutriagent-config-card"
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 350, damping: 28 }}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              "relative w-full max-w-md overflow-hidden rounded-3xl",
              "bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-50",
              "border border-zinc-200 dark:border-zinc-800 shadow-2xl",
              illumination && "shadow-[0_0_40px_-10px_var(--brand-green)]"
            )}
          >
          {/* Header */}
          <div className="relative p-6 pb-4 border-b border-zinc-200 dark:border-zinc-800 flex items-start justify-between bg-white dark:bg-zinc-950">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                <Bot className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-50 tracking-tight">
                  {lang === 'ua' ? 'НАЛАШТУВАННЯ NUTRI-AGENT' : 'NUTRI-AGENT SETUP'}
                </h2>
                <p className="text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                  {lang === 'ua' ? 'Преміум-доставка продуктів' : 'Premium Fulfillment'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-900 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-200 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto no-scrollbar">
            {/* Store Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-zinc-900 dark:text-zinc-300 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-emerald-500" />
                  {lang === 'ua' ? 'Оберіть магазин' : 'Target Store'}
                </label>
                
                {/* Region selector tabs */}
                <div className="flex gap-1 bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-1 rounded-xl">
                  <button
                    type="button"
                    onClick={() => handleSetStoreRegion('ua')}
                    className={cn(
                      "px-2.5 py-1 text-[11px] font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1",
                      storeRegion === 'ua'
                        ? "bg-emerald-500 text-white shadow-xs"
                        : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                    )}
                  >
                    <span>🇺🇦</span>
                    <span>{lang === 'ua' ? 'Україна' : 'Ukraine'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSetStoreRegion('intl')}
                    className={cn(
                      "px-2.5 py-1 text-[11px] font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1",
                      storeRegion === 'intl'
                        ? "bg-emerald-500 text-white shadow-xs"
                        : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                    )}
                  >
                    <span>🇺🇸</span>
                    <span>USA</span>
                  </button>
                </div>
              </div>

              {/* Stores Horizontal Scroll */}
              <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1 scroll-smooth">
                {(storeRegion === 'ua' ? UKRAINIAN_STORES : INTERNATIONAL_STORES).map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => handleSelectStore(s.id, storeRegion === 'ua')}
                    className={cn(
                      "px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all border shrink-0 cursor-pointer whitespace-nowrap",
                      store === s.id 
                        ? "bg-emerald-500/10 border-emerald-500 text-emerald-600 dark:text-emerald-400 font-bold shadow-xs ring-1 ring-emerald-500/30" 
                        : "bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-700 dark:text-zinc-300 hover:border-emerald-500/50 hover:text-zinc-900 dark:hover:text-zinc-100"
                    )}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {/* Store Login / App Bind Status Card */}
              <div className="p-3 rounded-2xl bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className={cn(
                    "w-8 h-8 rounded-xl flex items-center justify-center shrink-0 font-bold text-xs",
                    connectedStoreData ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/30" : "bg-amber-500/10 text-amber-500 border border-amber-500/30"
                  )}>
                    {connectedStoreData ? <CheckCircle2 className="w-4 h-4" /> : <Key className="w-4 h-4" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-zinc-900 dark:text-zinc-100 truncate">
                      {getConnectedAccountLabel()}
                    </p>
                    <p className="text-[10px] text-zinc-500 dark:text-zinc-400 flex items-center gap-1 truncate">
                      <ShieldCheck className="w-3 h-3 text-emerald-500 shrink-0" />
                      <span>
                        {connectedStoreData?.type === 'app' || connectedStoreData?.appBound
                          ? (lang === 'ua' ? `Зв'язано додаток ${getStoreDisplayName(store)}` : `${getStoreDisplayName(store)} App Synced`)
                          : (lang === 'ua' ? '256-Bit Encrypted Vault' : '256-Bit Encrypted Vault')}
                      </span>
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setIsStoreLoginOpen(true)}
                  className="px-3 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs transition-all cursor-pointer shrink-0 shadow-xs"
                >
                  {connectedStoreData ? (lang === 'ua' ? 'Змінити' : 'Manage') : (lang === 'ua' ? 'Увійти / Додаток' : 'Login / App')}
                </button>
              </div>
            </div>

            {/* Fulfillment & Store Delivery Minimum Progress Card */}
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-zinc-900 dark:text-zinc-300 flex items-center gap-2">
                  <Truck className="w-4 h-4 text-emerald-500" />
                  <span>{lang === 'ua' ? 'Отримання' : 'Fulfillment'}</span>
                </label>

                {/* Fulfillment Selector Tabs */}
                <div className="flex gap-1 bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-1 rounded-xl">
                  <button
                    type="button"
                    onClick={() => setFulfillmentMethod('delivery')}
                    className={cn(
                      "px-2.5 py-1 text-[11px] font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1",
                      fulfillmentMethod === 'delivery'
                        ? "bg-emerald-500 text-white shadow-xs"
                        : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                    )}
                  >
                    <Truck className="w-3 h-3" />
                    <span>{lang === 'ua' ? 'Доставка' : 'Delivery'}</span>
                  </button>

                  {storeThreshold.pickupAvailable && (
                    <button
                      type="button"
                      onClick={() => setFulfillmentMethod('pickup')}
                      className={cn(
                        "px-2.5 py-1 text-[11px] font-bold rounded-lg transition-all cursor-pointer flex items-center gap-1",
                        fulfillmentMethod === 'pickup'
                          ? "bg-emerald-500 text-white shadow-xs"
                          : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                      )}
                    >
                      <Store className="w-3 h-3" />
                      <span>{lang === 'ua' ? 'Самовивіз (безкоштовно)' : 'Free Pickup'}</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Store Minimum Progress & Top-Up Bar */}
              {fulfillmentMethod === 'delivery' ? (
                <div className={cn(
                  "p-3 rounded-2xl border transition-all space-y-2",
                  isBelowMinDelivery
                    ? "bg-amber-500/10 border-amber-500/30"
                    : "bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800"
                )}>
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5 font-bold">
                      {isBelowMinDelivery ? (
                        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                      )}
                      <span className={isBelowMinDelivery ? "text-amber-600 dark:text-amber-400 font-extrabold" : "text-zinc-900 dark:text-zinc-100"}>
                        {isBelowMinDelivery 
                          ? (lang === 'ua' 
                              ? `До мінімуму доставки бракує ${currentSymbol}${deficitToMin}` 
                              : `Need +${currentSymbol}${deficitToMin} for delivery min`)
                          : (lang === 'ua'
                              ? `Мінімум доставки (${currentSymbol}${storeThreshold.minDeliverySum}) виконано`
                              : `Delivery minimum (${currentSymbol}${storeThreshold.minDeliverySum}) met`)}
                      </span>
                    </div>
                    <span className="text-[11px] font-mono font-bold text-zinc-600 dark:text-zinc-400">
                      {currentSymbol}{effectiveSubtotal.toFixed(0)} / {currentSymbol}${storeThreshold.minDeliverySum}
                    </span>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full h-2 rounded-full bg-zinc-200 dark:bg-zinc-800 overflow-hidden">
                    <div 
                      className={cn(
                        "h-full transition-all duration-500 rounded-full",
                        isBelowMinDelivery 
                          ? "bg-gradient-to-r from-amber-500 to-amber-400" 
                          : "bg-gradient-to-r from-emerald-500 to-emerald-400"
                      )}
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>

                  {/* Action buttons if below delivery minimum */}
                  {isBelowMinDelivery && (
                    <div className="flex items-center gap-1.5 pt-1">
                      <button
                        type="button"
                        onClick={handleAutoTopUpWithStaples}
                        className="flex-1 py-1.5 px-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-[11px] flex items-center justify-center gap-1 transition-all cursor-pointer shadow-xs active:scale-95"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        <span>{lang === 'ua' ? `⚡ Доповнити дефіцитами (+${currentSymbol}${deficitToMin})` : `⚡ Auto-Top Up (+${currentSymbol}${deficitToMin})`}</span>
                      </button>

                      {storeThreshold.pickupAvailable && (
                        <button
                          type="button"
                          onClick={() => {
                            setFulfillmentMethod('pickup');
                            toast.info(lang === 'ua' ? 'Перемкнуто на Самовивіз (без мінімальної суми)' : 'Switched to Pickup (no order minimum)');
                          }}
                          className="py-1.5 px-2.5 rounded-xl bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 text-zinc-800 dark:text-zinc-200 font-bold text-[11px] transition-all cursor-pointer whitespace-nowrap"
                        >
                          {lang === 'ua' ? '🏬 Самовивіз (безкоштовно)' : '🏬 Free Pickup'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-2.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-between text-xs font-bold text-emerald-600 dark:text-emerald-400">
                  <div className="flex items-center gap-2">
                    <Store className="w-4 h-4 text-emerald-500" />
                    <span>{lang === 'ua' ? 'Самовивіз у найближчому магазині: 0 ₴ мінімум, 0 ₴ доставка' : 'Pickup at store: 0 minimum, free pickup'}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFulfillmentMethod('delivery')}
                    className="text-[10px] underline hover:text-emerald-300 cursor-pointer"
                  >
                    {lang === 'ua' ? 'Змінити на доставку' : 'Change to delivery'}
                  </button>
                </div>
              )}
            </div>

            {/* Budget Limit & Currency */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-zinc-900 dark:text-zinc-300 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-500" />
                  <span>{lang === 'ua' ? 'Бюджетний ліміт' : 'Budget Limit'}</span>
                  {groceryListMode === 'only_grocery' && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 flex items-center gap-1">
                      <span>{lang === 'ua' ? 'Авто-розрахунок' : 'Auto-Calculated'}</span>
                    </span>
                  )}
                </label>
                <div className="flex gap-1 bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-1 rounded-xl">
                  {availableCurrencies.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCurrency(c)}
                      className={cn(
                        "px-2.5 py-0.5 text-xs font-bold rounded-lg transition-all cursor-pointer",
                        currency === c 
                          ? "bg-emerald-500 text-white shadow-xs" 
                          : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                      )}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-zinc-500 dark:text-zinc-400 font-bold sm:text-sm">{currentSymbol}</span>
                </div>
                <input
                  type="number"
                  value={budget}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '') {
                      setBudget('');
                    } else {
                      const num = parseFloat(val);
                      setBudget(isNaN(num) ? '' : num);
                    }
                  }}
                  className={cn(
                    "block w-full pl-8 pr-16 py-3 sm:text-sm rounded-xl transition-all font-semibold",
                    "bg-zinc-50 dark:bg-zinc-900",
                    "border border-zinc-200 dark:border-zinc-800",
                    "text-zinc-900 dark:text-zinc-50 placeholder-zinc-400 dark:placeholder-zinc-500 placeholder:italic",
                    "focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                  )}
                  placeholder={lang === 'ua' ? "3000" : "80.00"}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-zinc-500 dark:text-zinc-400 font-bold sm:text-sm">{currency}</span>
                </div>
              </div>

              {groceryListMode === 'only_grocery' && (
                <div className="flex items-center justify-between text-[10px] text-zinc-500 dark:text-zinc-400 px-1 pt-0.5">
                  <span>
                    {lang === 'ua' 
                      ? `Оцінка ${unboughtItems.length} тов. + доставка (~${currentSymbol}${estimatedListTotal})` 
                      : `Estimated for ${unboughtItems.length} items + delivery (~${currentSymbol}${estimatedListTotal})`}
                  </span>
                  {budget !== estimatedListTotal && (
                    <button
                      type="button"
                      onClick={() => setBudget(estimatedListTotal)}
                      className="text-emerald-500 font-bold hover:underline cursor-pointer ml-2"
                    >
                      {lang === 'ua' ? 'Скинути до авто-суми' : 'Reset to estimate'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Grocery List Integration Mode Selector */}
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-zinc-900 dark:text-zinc-300 flex items-center gap-2">
                  <ListChecks className="w-4 h-4 text-emerald-500" />
                  {lang === 'ua' ? 'Джерело продуктів' : 'Grocery Food Source'}
                </label>
                {unboughtItems.length > 0 && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                    {unboughtItems.length} {lang === 'ua' ? 'в списку' : 'in app list'}
                  </span>
                )}
              </div>

              {/* 3 Option Buttons */}
              <div className="grid grid-cols-3 gap-1.5 p-1 rounded-2xl bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800">
                <button
                  type="button"
                  onClick={() => handleSetGroceryListMode('all_ai')}
                  className={cn(
                    "py-2 px-1 rounded-xl text-[11px] font-bold transition-all text-center flex flex-col items-center justify-center gap-1 cursor-pointer",
                    groceryListMode === 'all_ai'
                      ? "bg-emerald-500 text-white shadow-xs font-black"
                      : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white dark:hover:bg-zinc-800"
                  )}
                >
                  <Sparkles className="w-3.5 h-3.5 shrink-0" />
                  <span className="leading-tight">{lang === 'ua' ? 'ШІ-Дефіцити' : 'AI Deficits'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleSetGroceryListMode('include_grocery')}
                  className={cn(
                    "py-2 px-1 rounded-xl text-[11px] font-bold transition-all text-center flex flex-col items-center justify-center gap-1 cursor-pointer",
                    groceryListMode === 'include_grocery'
                      ? "bg-emerald-500 text-white shadow-xs font-black"
                      : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white dark:hover:bg-zinc-800"
                  )}
                >
                  <div className="flex items-center gap-0.5">
                    <Sparkles className="w-3 h-3 shrink-0" />
                    <span>+</span>
                    <ListChecks className="w-3 h-3 shrink-0" />
                  </div>
                  <span className="leading-tight">{lang === 'ua' ? 'Включити список' : 'Include List'}</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleSetGroceryListMode('only_grocery')}
                  className={cn(
                    "py-2 px-1 rounded-xl text-[11px] font-bold transition-all text-center flex flex-col items-center justify-center gap-1 cursor-pointer",
                    groceryListMode === 'only_grocery'
                      ? "bg-emerald-500 text-white shadow-xs font-black"
                      : "text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white dark:hover:bg-zinc-800"
                  )}
                >
                  <ShoppingCart className="w-3.5 h-3.5 shrink-0" />
                  <span className="leading-tight">{lang === 'ua' ? 'Лише мій список' : 'Only My List'}</span>
                </button>
              </div>

              {/* Dynamic Description & Items Preview */}
              <div className="text-[11px] p-2.5 rounded-xl bg-zinc-50 dark:bg-zinc-900/90 border border-zinc-200 dark:border-zinc-800 space-y-1.5">
                <div className="text-zinc-600 dark:text-zinc-300 font-medium leading-relaxed">
                  {groceryListMode === 'only_grocery' ? (
                    <span>
                      {lang === 'ua' 
                        ? 'Nutri-Agent замовить ' 
                        : 'Nutri-Agent will buy '}
                      <strong>{lang === 'ua' ? `виключно товари зі списку додатку (${unboughtItems.length})` : `only the items from your app list (${unboughtItems.length})`}</strong>.
                    </span>
                  ) : groceryListMode === 'include_grocery' ? (
                    <span>
                      {lang === 'ua' 
                        ? 'Nutri-Agent обʼєднає ' 
                        : 'Nutri-Agent will combine '}
                      <strong>{lang === 'ua' ? `ваш список покупок (${unboughtItems.length})` : `your app grocery list (${unboughtItems.length})`}</strong>
                      {lang === 'ua' ? ' разом із продуктами під виявлені дефіцити.' : ' alongside core deficiency essentials.'}
                    </span>
                  ) : (
                    <span>
                      {lang === 'ua' 
                        ? 'Nutri-Agent автономно підбере кошик суто під ваші дефіцити білка, клітковини та мікроелементів.' 
                        : 'Nutri-Agent will autonomously balance essentials based on biological deficit telemetry.'}
                    </span>
                  )}
                </div>

                {groceryListMode !== 'all_ai' && unboughtItems.length > 0 && (
                  <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto no-scrollbar pt-1 border-t border-zinc-200/60 dark:border-zinc-800/60">
                    {unboughtItems.slice(0, 8).map((it: any, idx: number) => (
                      <span key={idx} className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 truncate max-w-[140px]">
                        {it.name} {it.amount ? `(${it.amount}${it.unit || ''})` : ''}
                      </span>
                    ))}
                    {unboughtItems.length > 8 && (
                      <span className="text-[10px] font-bold text-zinc-400 px-1 self-center">
                        +{unboughtItems.length - 8} {lang === 'ua' ? 'ще' : 'more'}
                      </span>
                    )}
                  </div>
                )}

                {groceryListMode === 'only_grocery' && unboughtItems.length === 0 && (
                  <p className="text-[10px] text-amber-500 font-semibold pt-1 border-t border-zinc-200/60 dark:border-zinc-800/60">
                    {lang === 'ua' ? 'У списку покупок додатку зараз немає товарів. Додайте товари у вкладці Продукти або виберіть ШІ-Дефіцити.' : 'Your app grocery list is currently empty. Add foods in the Groceries tab or switch to AI Deficits.'}
                  </p>
                )}
              </div>
            </div>

            {/* Special Instructions & Order Wishes Comment Box */}
            <div className="space-y-2">
              <label className="text-sm font-semibold text-zinc-900 dark:text-zinc-300 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-emerald-500" />
                  {lang === 'ua' ? 'Побажання до замовлення' : 'Special Order Instructions'}
                </span>
                <span className="text-[10px] text-zinc-400 font-mono">
                  {lang === 'ua' ? 'Опціонально' : 'Optional'}
                </span>
              </label>
              <textarea
                value={userNotes}
                onChange={(e) => setUserNotes(e.target.value)}
                rows={2}
                placeholder={lang === 'ua' 
                  ? "Наприклад: мигдалеве молоко без цукру, енергетик Monster Ultra, 2 банани..." 
                  : "e.g. Sugar-free almond milk, Monster Ultra Zero, 2 organic bananas..."
                }
                className={cn(
                  "w-full p-3 rounded-xl text-xs font-medium transition-all resize-none",
                  "bg-zinc-50 dark:bg-zinc-900",
                  "border border-zinc-200 dark:border-zinc-800",
                  "text-zinc-900 dark:text-zinc-50 placeholder-zinc-400 dark:placeholder-zinc-500 placeholder:italic",
                  "focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none"
                )}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 pt-4 border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50">
            {(userTier === 'premium' || userTier === 'premium_pro') ? (
              <button
                type="button"
                onClick={() => onLaunch({ 
                  budgetLimitUSD: typeof budget === 'number' ? budget : 0, 
                  currency, 
                  currencySymbol: currentSymbol, 
                  targetStores: [store], 
                  messengerPlatform: ['app_push'],
                  notificationChannels: ['app_push'],
                  userNotes: userNotes.trim() || undefined,
                  groceryListMode,
                  groceryItems: unboughtItems.map((i: any) => ({
                    id: i.id,
                    name: i.name,
                    amount: i.amount,
                    unit: i.unit,
                    checked: i.checked
                  })),
                  fulfillmentMethod
                })}
                className={cn(
                  "w-full py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 cursor-pointer",
                  "bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700",
                  "text-white font-semibold transition-all shadow-lg shadow-emerald-500/20"
                )}
              >
                <ShoppingCart className="w-5 h-5" />
                {lang === 'ua' ? 'Запустити Nutri-Agent' : 'Initialize Nutri-Agent'}
                <ArrowRight className="w-4 h-4 ml-1" />
              </button>
            ) : (
              <div className="text-center space-y-2">
                <div className="flex items-center justify-center gap-1.5 text-xs font-bold text-amber-500">
                  <Key className="w-4 h-4" />
                  <span className="whitespace-nowrap">{lang === 'ua' ? 'ПОТРІБЕН PREMIUM' : 'PREMIUM REQUIRED'}</span>
                </div>
                <p className="text-[10px] text-zinc-500">
                  {lang === 'ua' 
                    ? 'Автономне замовлення доступне ексклюзивно у тарифі PREMIUM (Smart Grocery Planner доступний у PRO).' 
                    : 'Autonomous shopping is exclusively available in PREMIUM (Smart Grocery Planner is available in PRO).'}
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>

  <StoreLoginModal
    isOpen={isStoreLoginOpen}
    onClose={() => setIsStoreLoginOpen(false)}
    targetStore={store}
    lang={lang}
    theme={theme}
  />
  </>
  );
};
